import { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Clock, Shovel } from "lucide-react";
import api from "@/lib/api";
import { AREAS, PHASE_MAP, ERAS, eraOfYear, phaseBadge } from "@/lib/phases";

const fmtYear = (yr) => {
  const a = Math.abs(yr).toLocaleString();
  return yr < 0 ? `${a} BCE` : `${a} CE`;
};

export default function Timeline() {
  const [items, setItems] = useState([]);
  const [area, setArea] = useState("all");
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const eraRefs = useRef({});

  useEffect(() => {
    setLoading(true);
    const params = { ...(area !== "all" ? { area } : {}), ...(showAll ? { stubs: "show" } : {}) };
    api.get("/timeline", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [area, showAll]);

  // Which eras are actually present (in chronological order), with a count each.
  const presentEras = useMemo(() => {
    const counts = {};
    items.forEach((it) => { const k = eraOfYear(it.year_est).key; counts[k] = (counts[k] || 0) + 1; });
    return ERAS.filter((e) => counts[e.key]).map((e) => ({ ...e, count: counts[e.key] }));
  }, [items]);

  const jumpToEra = (key) => {
    const el = eraRefs.current[key];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-8 lg:px-12 py-10">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-1.5">The Archive · Chronology</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground flex items-center gap-3">
        <Clock className="h-7 w-7 text-accent" /> Timeline
      </h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">
        Every <span className="text-foreground">dated</span> entry, laid out from the deepest past to the present.
        Most imported sites have no date on record yet, so they can't be plotted here — running an entry's AI
        deep-dive fills in its age and adds it to the timeline.
      </p>

      {/* Area filter */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="timeline-area-filters">
        <button
          data-testid="timeline-area-all"
          onClick={() => setArea("all")}
          className="px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-colors"
          style={{
            borderColor: area === "all" ? "hsl(var(--foreground))" : "hsl(var(--border))",
            background: area === "all" ? "hsl(var(--foreground))" : "transparent",
            color: area === "all" ? "hsl(var(--background))" : "hsl(var(--muted-foreground))",
          }}
        >
          All Areas
        </button>
        {AREAS.map((a) => (
          <button
            key={a.key}
            data-testid={`timeline-area-${a.key}`}
            onClick={() => setArea(a.key)}
            className="px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-colors"
            style={{
              borderColor: area === a.key ? a.color : "hsl(var(--border))",
              background: area === a.key ? a.color : "transparent",
              color: area === a.key ? "#fff" : "hsl(var(--muted-foreground))",
            }}
          >
            {a.label}
          </button>
        ))}
        <div className="ml-auto flex border border-border" data-testid="timeline-catalogue-toggle">
          <button
            data-testid="timeline-curated"
            onClick={() => setShowAll(false)}
            className="px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors"
            style={{
              background: !showAll ? "hsl(var(--foreground))" : "transparent",
              color: !showAll ? "hsl(var(--background))" : "hsl(var(--muted-foreground))",
            }}
          >
            Curated
          </button>
          <button
            data-testid="timeline-all"
            onClick={() => setShowAll(true)}
            className="px-4 py-2 font-mono text-xs uppercase tracking-wider border-l border-border transition-colors"
            style={{
              background: showAll ? "hsl(var(--foreground))" : "transparent",
              color: showAll ? "hsl(var(--background))" : "hsl(var(--muted-foreground))",
            }}
          >
            All
          </button>
        </div>
      </div>

      {/* Era jump band */}
      {!loading && presentEras.length > 1 && (
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="timeline-era-bands">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Jump to era</span>
          {presentEras.map((e) => (
            <button
              key={e.key}
              data-testid={`timeline-era-jump-${e.key}`}
              onClick={() => jumpToEra(e.key)}
              className="group px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider border border-border text-muted-foreground hover:border-accent hover:text-accent transition-colors"
            >
              {e.label} <span className="opacity-60">· {e.count}</span>
            </button>
          ))}
        </div>
      )}

      <p className="mt-6 font-mono text-xs uppercase tracking-widest text-muted-foreground">
        {loading ? "Loading…" : `${items.length} dated entries`}
        {!loading && showAll && (
          <span className="ml-2 normal-case tracking-normal text-muted-foreground/70">· undated imported sites aren't shown</span>
        )}
      </p>

      {/* Timeline spine */}
      <div className="mt-4 relative" data-testid="timeline-list">
        <div className="absolute left-[7.5rem] top-0 bottom-0 w-px bg-border hidden sm:block" />
        <ol className="space-y-4">
          {items.map((it, idx) => {
            const color = (PHASE_MAP[it.phase] || PHASE_MAP.pyramids).color;
            const era = eraOfYear(it.year_est);
            const prevEra = idx > 0 ? eraOfYear(items[idx - 1].year_est).key : null;
            const isEraStart = era.key !== prevEra;
            return (
              <li
                key={it.slug}
                className="flex items-stretch gap-0 sm:gap-5"
                data-testid={`timeline-item-${it.slug}`}
                ref={isEraStart ? (el) => { eraRefs.current[era.key] = el; } : undefined}
              >
                <div className="hidden sm:flex w-28 shrink-0 flex-col items-end justify-center pr-1">
                  {isEraStart && (
                    <span className="mb-1 font-mono text-[9px] uppercase tracking-widest text-accent" data-testid={`timeline-era-label-${era.key}`}>
                      {era.label}
                    </span>
                  )}
                  <span className="font-mono text-xs font-semibold text-foreground">{fmtYear(it.year_est)}</span>
                </div>
                <div className="relative hidden sm:flex w-4 shrink-0 items-center justify-center">
                  <span className="h-3 w-3 rounded-full border-2 border-background" style={{ background: color }} />
                </div>
                <Link
                  to={`/pyramid/${it.slug}`}
                  className="group flex-1 flex gap-4 border border-border bg-card p-4 hover:border-accent hover:-translate-y-0.5 transition-all"
                >
                  {it.image ? (
                    <img src={it.image} alt="" loading="lazy" className="h-16 w-16 shrink-0 object-cover border border-border" />
                  ) : (
                    <span className="h-16 w-16 shrink-0 flex items-center justify-center border border-border text-background font-mono text-[10px]" style={{ background: color }}>
                      {fmtYear(it.year_est).split(" ")[1]}
                    </span>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 text-white" style={{ background: color }}>
                        {phaseBadge(it.phase)}
                      </span>
                      <span className="sm:hidden font-mono text-[11px] font-semibold text-foreground">{fmtYear(it.year_est)}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <h3 className="font-serif text-lg font-semibold text-foreground leading-tight group-hover:text-accent">{it.name}</h3>
                      {it.deep_dive && (
                        <span
                          data-testid={`timeline-deep-dive-badge-${it.slug}`}
                          className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-accent/50 text-accent bg-accent/5"
                        >
                          <Shovel className="h-2.5 w-2.5" /> Deep Dive
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {it.country}{it.estimated_age ? ` · ${it.estimated_age}` : ""}
                    </p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
